/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getApplications, loadApplications as loadApps} from '@ion/core/src/services/Connect';
import {setRel} from '@ion/core/src/services/ConnectUtils';
import {Applications, CTCEntity} from '@ion/core/src/types/entities';
import {LoadApplicationsParams} from '@ion/core/src/types/services';
import Chip from '@ion/dls/src/components/chip/Chip';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import Dropdown, {createOptions, Options} from '@ion/dls/src/components/menus/Dropdown';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';

/**
 * Defines the prop required by this component.
 */
export type AccountApplicationsChooserProps = {
  accountId: number;
  onChange?: (subscription: CTCEntity) => void;
  readOnly?: boolean;
  required?: boolean;
  includeNone?: boolean;
  multiSelect?: boolean;
  hasError?: boolean;
  errorText?: string;
  classes?: string;
  defaultValue?: any;
  isCtcAdmin?: boolean;
  subscribedApplications?: Applications;
  setApplicationList?: Function;
  isChipView?: boolean;
};

const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  },
  boxStyle : {
    display   : 'flex',
    marginTop : '14px',
    flexWrap  : 'wrap',
    rowGap    : '10px'
  },
  boxfieldLabel : {
    color        : dlsColor('grey2'),
    marginBottom : '8px',
    ...dlsTypography('body3')
  }
}));

/**
 * The AccountApplicationsChooser provides a list of applications for display within a dropdown list
 * allowing the user to select a application from the list.
 *
 * ```typescript
 *
 *     import {
 *       AccountApplicationsChooser
 *     } from '@ion/features/src/entities/account-applications/AccountApplicationsChooser';
 *
 *     <AccountApplicationsChooser defaultValue={application}
 *        accountId={accountId}
 *        errorText=""
 *        onChange={selectedApplicationCallback} />
 *
 * ```
 *
 * @accountId        Allows for providing an account id for which the subscription list is fetched.
 * @onChange         Callback prop which will be invoked with the selected Subscription.
 * @readOnly         When true, the component will be displayed in read-only mode;
 *                   that is, disabled. Defaults to false.
 * @required         When true, the required indicator will be displayed.
 * @errorText  When true, the errorText indicator will be displayed.
 * @classes          Optional classes to apply to the wrapping element.
 * @includeNone      When true (default), localized 'None' option will be displayed.
 * @defaultValue     Default Value of the field
 */
export const AccountApplicationsChooser = ({
  accountId,
  onChange,
  readOnly,
  required,
  classes,
  hasError,
  errorText,
  defaultValue,
  subscribedApplications,
  setApplicationList,
  includeNone = true,
  multiSelect = false,
  isCtcAdmin = false,
  isChipView = false
}: AccountApplicationsChooserProps) => {
  const _classes                          = useStyles();
  const [applications, setApplications]   = useState<CTCEntity[]>(null);
  const [isLoading, setIsLoading]         = useState<boolean>(false);
  const [options, setOptions]             = useState<Options[]>([]);
  const [selected, setSelected]           = useState<string[]>([]);
  const hasMounted                        = useHasMounted();

  // whenever the account changes the applications are reloaded ...
  useEffect(() => {
    const loadApplications = async(accountId: number) => {
      setIsLoading(true);
      if (hasMounted !== false) {
        try {
          const results = await getApplications(accountId);
          if (results?.length) {
            const applicationsList = sortApplications(results);
            setApplicationList?.(results);
            setApplications(applicationsList);
            setOptions(createOptions({
              items     : applicationsList,
              valProp   : 'id',
              labelProp : 'name'
            }));
          }
        } catch (err) {
          error(`ApplicationsChooser: Error occurred retrieving Subscriptions for account ${accountId}`);
          error(err);
        } finally {
          setIsLoading(false);
        }
      }
    };

    accountId && loadApplications(accountId);
  }, [accountId]);

  /**
   * Generate the Payload to retrieve the Application
   * and use the search API to retrieve the applications
   * by its account id
   */
  const searchApplications = async() => {
    const data: LoadApplicationsParams = {
      accountId : accountId,
      pageId    : 1,
      pageSize  : 100
    };
    return (await loadApps(data))?.application;
  };

  // if a default application is provided set it as the selected ...
  useEffect(() => {
    if (defaultValue?.length) {
      setSelected(defaultValue);
    }
  }, [defaultValue]);

  /**
  * Sorts the Applications before populating the chooser.
  */
  const sortApplications = (applications): CTCEntity[] => {
    return applications.sort((a, b) => a.name > b.name ? 1 : -1);
  };

  /**
  * Provides a utility which filters a list of applications for the currently selected id.
  */
  const getSelectedValue = (selectedKey: any) => {
    if (multiSelect) {
      const idValues = selectedKey?.map(({label}) => label) || [];
      const selectedApplications = applications.filter((item) => {
        return idValues.includes(item?.name) || selectedKey.includes(item.name);
      });
      return (
        selectedApplications?.map((application) => {
          const hrefObj = setRel(application, 'application');
          return {...hrefObj, title: application?.name};
        }) || []
      );
    }
    return applications.find(({name}: CTCEntity) => name === selectedKey);
  };

  //Setting dropdown value
  const getDropdownValue = () => {
    if (isLoading) {
      return multiSelect ? [translate('common:states.loading')] : translate('common:states.loading');
    }
    return selected;
  };

  //Filter Application based on the users subscription.
  const filterApplication = useCallback((appList) => {
    return appList?.filter(({_self}) => subscribedApplications?.map(({href}) => href).includes(_self.href));
  }, []);
  if (readOnly && isChipView) {
    return (
      <FormItem fullWidth>
        <BodyTypography className={_classes.boxfieldLabel}>
          {translate('common:features.applications.label')}
        </BodyTypography>
        <div className={_classes.boxStyle}>
          {selected?.map((app, index) => (
            <div key={`${app}_${index}`}>
              <Chip label={app}
                size='small'
                maxWidth={false}
                borderRadius='56px'
                labelColor={dlsColor('grey2')}
                hoverDefault
                showCustomDeleteIcon
                noTextTransform />
            </div>
          ))}
        </div>
      </FormItem>
    );
  }
  return (
    <div className={classes || _classes.root}>
      <Dropdown displaySelectedLabel
        includeNone={includeNone}
        multiSelect={multiSelect}
        required={required}
        disabled={readOnly || isLoading || options?.length === 0}
        hasError={hasError}
        options={options}
        value={getDropdownValue()}
        fieldLabel={translate('common:features.applications.label')}
        placeholder={translate('common:features.applications.accountPlaceholder')}
        errorText={errorText}
        preferPlaceholder={!isLoading && !selected}
        onChange={onChange ? function({target}) {
          const id = target.value as any;
          setSelected(id);
          onChange(getSelectedValue(id));
        } : null} />
    </div>
  );
};
export default AccountApplicationsChooser;
